import * as React from 'react'
import { useState } from 'react'
import { Button, Text, Tooltip } from '@fluentui/react-components'
import {
    Building20Regular,
    BuildingMultiple20Regular,
    bundleIcon,
    DocumentTableFilled,
    DocumentTableRegular,
    Person20Regular,
    ShieldPerson20Regular,
} from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { BaseOverviewLineItemProps, useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { isPartyOverviewItem, ListItemDto, PartyOverviewItemDto } from '../../../config/partyConfig'
import { PartyStatus, PartyType } from '../../../components/Form/Party/PartyForm'
import { PartyFilterType, PartySortingType, useItemStyles } from './BasePartyOverview'
import useSubmitDocumentGenerationRequest from '../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import { DocumentGenerationType } from '../../../hooks/document/documentGenerationTypes'
import { PartyCardReportDto } from '../../../components/Form/Finance/Report/PartyCardReportForm'
import PersonAvatar from '../../../components/PersonAvatar'
import PhoneNumberButton from '../../components/Button/PhoneNumberButton'
import EmailButton from '../../components/Button/EmailButton'
import { composeEmailClick } from '../../../helpers/partyComposeHelper'
import PartyCardPopover from './PartyCardPopover'

const PartCardReportIcon = bundleIcon(DocumentTableFilled, DocumentTableRegular)

export const getPartyIcon = (party: ListItemDto): React.JSX.Element => {
    if (isPartyOverviewItem(party) && (party as PartyOverviewItemDto).identificationVerified) {
        return <ShieldPerson20Regular />
    }

    switch (party.type) {
        case PartyType.Legal: {
            return party.crmRelation ? <BuildingMultiple20Regular /> : <Building20Regular />
        }
        case PartyType.Natural: {
            return <Person20Regular />
        }
    }

    return undefined
}

export function PartyListItem(props: BaseOverviewLineItemProps<PartyOverviewItemDto, PartyFilterType, PartySortingType>) {
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const submitRequest = useSubmitDocumentGenerationRequest<PartyCardReportDto>(DocumentGenerationType.PartyCard)

    const { t: translate } = useTranslation()

    // The party card is anchored to its trigger, so the hover actions have to stay visible while it is open.
    const [cardOpen, setCardOpen] = useState(false)

    return (
        <>
            {props.item.accountManager && <PersonAvatar name={props.item.accountManager.fullName} color={'colorful'} size={24} />}
            <div>{getPartyIcon(props.item)}</div>
            <Text className={itemStyles.listPartyItemOverflow} data-type={props.item.status === PartyStatus.Inactive ? 'strikethrough' : 'open'}>
                {props.item.displayName || <em>Naam ontbreekt.</em>}
            </Text>
            <aside data-hover-actions={cardOpen ? undefined : 'display'} className={overviewStyles.flexGapXS}>
                {props.item.type === PartyType.Legal && (
                    <Tooltip relationship={'label'} content={translate('party.report.partycard')} withArrow>
                        <Button
                            icon={<PartCardReportIcon />}
                            appearance="secondary"
                            size={'small'}
                            onClick={() => submitRequest({ partyId: props.item.id })}
                        />
                    </Tooltip>
                )}
                <PartyCardPopover party={props.item} onOpenChange={setCardOpen} />
            </aside>
            <div data-hover-contact className={itemStyles.listPartyItemContact}>
                {props.item.phoneNumber && <PhoneNumberButton clickValue={props.item.phoneNumber} withTooltip />}
                {props.item.email && <EmailButton tooltipValue={props.item.email} onClick={() => composeEmailClick(props.item)()} withTooltip />}
            </div>
        </>
    )
}
